import { formatMinor, type PayrollRun, type SavePayrollRunRequest } from '@hvac/contracts';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Field,
  Input,
  PageHeader,
  Skeleton,
} from '@hvac/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { bookkeepingReferenceQuery } from '../../shared/api/bookkeeping-queries.js';
import {
  payrollKeys,
  payrollLiabilitiesQuery,
  payrollRunsQuery,
  postPayrollRun,
  savePayrollRun,
  voidPayrollRun,
} from '../../shared/api/payroll-queries.js';
import { sessionQuery } from '../../shared/api/queries.js';
import { dollarsToMinor, minorToDollars } from '../books/books-format.js';

/**
 * Payroll as the books see it. A pay run is typed in or arrives read from
 * the register dropped in the Inbox; either way it is a draft until a
 * person posts it, and posting writes one balanced entry: wages and the
 * employer's share as expense, every deduction as a liability. The
 * liabilities card is the reconciliation: net pay and remittances explained
 * on the Bank page bring it back to zero.
 */

interface LineDraft {
  key: number;
  employeeName: string;
  technicianId: string;
  gross: string;
  cpp: string;
  ei: string;
  tax: string;
  other: string;
  employerCpp: string;
  employerEi: string;
  employerOther: string;
}

const blankLine = (key: number): LineDraft => ({
  key,
  employeeName: '',
  technicianId: '',
  gross: '',
  cpp: '',
  ei: '',
  tax: '',
  other: '',
  employerCpp: '',
  employerEi: '',
  employerOther: '',
});

const today = () => new Date().toISOString().slice(0, 10);
const cents = (dollars: string): bigint => BigInt(dollarsToMinor(dollars) ?? '0');

export function PayrollPage() {
  const { data: session } = useSuspenseQuery(sessionQuery());
  const organizationId = session.activeOrganizationId ?? '';
  const membership = session.organizations.find(
    (entry) => entry.organization.id === organizationId,
  );
  const canWrite = membership?.permissions.includes('transaction:update') ?? false;
  const queryClient = useQueryClient();
  const runs = useQuery(payrollRunsQuery(organizationId));
  const liabilities = useQuery(payrollLiabilitiesQuery(organizationId));
  const [editing, setEditing] = useState<PayrollRun | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: payrollKeys.all(organizationId) });
    void queryClient.invalidateQueries({ queryKey: ['reports', organizationId] });
  };

  const post = useMutation({
    mutationFn: (runId: string) => postPayrollRun(organizationId, runId),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'The run was not posted.'),
  });

  const rows = runs.data ?? [];
  const drafts = rows.filter((row) => row.status === 'draft');
  const owed = liabilities.data;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Business"
        title="Payroll"
        description={
          runs.data === undefined
            ? 'Reading…'
            : rows.length === 0
              ? 'No pay runs yet. Drop the payroll register from Wagepoint, QuickBooks Payroll or ADP into the Inbox and it arrives here read and proved, or enter a run by hand.'
              : drafts.length === 0
                ? `${String(rows.length)} pay run${rows.length === 1 ? '' : 's'}, all posted.`
                : `${String(drafts.length)} draft${drafts.length === 1 ? '' : 's'} waiting to be posted.`
        }
        action={
          canWrite && editing === null ? (
            <Button onClick={() => setEditing('new')}>Enter a pay run</Button>
          ) : undefined
        }
      />

      {error === null ? null : <Alert tone="danger">{error}</Alert>}

      <Card>
        <CardHeader
          title="What payroll still owes"
          description="Posted but not yet seen leaving the bank. Explain the net pay and the CRA remittance on the Bank page and these come back to zero."
        />
        <CardBody>
          {owed === undefined ? (
            <Skeleton className="h-10 w-full" />
          ) : (
            <dl className="grid gap-4 text-sm sm:grid-cols-3">
              <Owed label="To the CRA (source deductions)" value={owed.sourceDeductionsMinor} />
              <Owed label="To employees (net pay)" value={owed.netPayOwedMinor} />
              <Owed label="Other (benefits, WCB, garnishments)" value={owed.otherMinor} />
            </dl>
          )}
        </CardBody>
      </Card>

      {editing === null ? null : (
        <RunEditor
          organizationId={organizationId}
          initial={editing === 'new' ? null : editing}
          onDone={() => {
            setEditing(null);
            refresh();
          }}
          onCancel={() => setEditing(null)}
        />
      )}

      <Card>
        <CardHeader title="Pay runs" description="Drafts first, then newest first." />
        <CardBody className="flex flex-col gap-3">
          {runs.isPending ? (
            <Skeleton className="h-16 w-full" />
          ) : rows.length === 0 ? (
            <p className="text-sm text-content-muted">Nothing yet.</p>
          ) : (
            rows.map((run) => (
              <RunRow
                key={run.id}
                run={run}
                canWrite={canWrite}
                organizationId={organizationId}
                onEdit={() => setEditing(run)}
                onPost={() => post.mutate(run.id)}
                posting={post.isPending && post.variables === run.id}
                onChanged={refresh}
                onError={setError}
              />
            ))
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function Owed({ label, value }: { label: string; value: string }) {
  const negative = value.startsWith('-');
  return (
    <div>
      <dt className="text-xs text-content-muted">{label}</dt>
      <dd className={['text-lg font-semibold tabular', negative ? 'text-warning' : ''].join(' ')}>
        {formatMinor(value)}
        {negative ? (
          <span className="ml-1 text-xs font-normal text-content-muted">
            (paid more than posted)
          </span>
        ) : null}
      </dd>
    </div>
  );
}

function RunRow({
  run,
  canWrite,
  onEdit,
  onPost,
  posting,
  onChanged,
  onError,
  organizationId,
}: {
  run: PayrollRun;
  canWrite: boolean;
  onEdit: () => void;
  onPost: () => void;
  posting: boolean;
  onChanged: () => void;
  onError: (message: string | null) => void;
  organizationId: string;
}) {
  const [voiding, setVoiding] = useState(false);
  const [reason, setReason] = useState('');
  const doVoid = useMutation({
    mutationFn: () => voidPayrollRun(organizationId, run.id, reason.trim()),
    onSuccess: () => {
      setVoiding(false);
      onError(null);
      onChanged();
    },
    onError: (caught) =>
      onError(caught instanceof Error ? caught.message : 'The run was not voided.'),
  });
  const employerMinor =
    BigInt(run.employerCppMinor) + BigInt(run.employerEiMinor) + BigInt(run.employerOtherMinor);
  const tone =
    run.status === 'draft'
      ? 'text-warning'
      : run.status === 'posted'
        ? 'text-success'
        : 'text-content-muted';
  return (
    <div className="flex flex-col gap-2 border-t border-border pt-3 text-sm first:border-0 first:pt-0">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-medium">
            Paid {run.payDate}
            {run.periodStart === null
              ? ''
              : ` for ${run.periodStart} to ${run.periodEnd ?? run.periodStart}`}
            <span className={['ml-2 text-xs', tone].join(' ')}>
              {run.status === 'draft'
                ? 'draft'
                : run.status === 'posted'
                  ? 'posted'
                  : `voided: ${run.voidReason ?? ''}`}
            </span>
          </p>
          <p className="text-content-muted">
            {String(run.lines.length)} employee{run.lines.length === 1 ? '' : 's'} · gross{' '}
            {formatMinor(run.grossMinor)} · net {formatMinor(run.netMinor)} · deductions{' '}
            {formatMinor(
              (
                BigInt(run.cppMinor) +
                BigInt(run.eiMinor) +
                BigInt(run.taxMinor) +
                BigInt(run.otherMinor)
              ).toString(),
            )}{' '}
            · employer {formatMinor(employerMinor.toString())}
          </p>
          {run.documentId === null ? null : (
            <p className="text-xs">
              {run.readingStatus === 'balanced' ? (
                <span className="text-success">Read from the register and proved. </span>
              ) : (
                <span className="text-danger">
                  Read from the register but it does not add up:{' '}
                  {run.readingFailure ?? 'a line fails'}.{' '}
                </span>
              )}
              <Link
                to="/documents/$documentId"
                params={{ documentId: run.documentId }}
                className="text-brand-700 hover:underline"
              >
                Open the register
              </Link>
            </p>
          )}
        </div>
        {!canWrite ? null : (
          <div className="flex items-center gap-2">
            {run.status === 'draft' ? (
              <>
                <Button size="sm" variant="secondary" onClick={onEdit}>
                  Edit
                </Button>
                <Button
                  size="sm"
                  onClick={onPost}
                  isLoading={posting}
                  disabled={run.readingStatus === 'unbalanced'}
                >
                  Post
                </Button>
              </>
            ) : null}
            {run.status !== 'void' && !voiding ? (
              <button
                type="button"
                className="text-xs text-content-muted hover:text-danger"
                onClick={() => setVoiding(true)}
              >
                Void
              </button>
            ) : null}
          </div>
        )}
      </div>
      {voiding ? (
        <div className="flex flex-wrap items-center gap-2">
          <Input
            aria-label="Why"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Why it is being voided"
            className="min-w-64 flex-1"
          />
          <Button
            size="sm"
            variant="danger"
            onClick={() => doVoid.mutate()}
            isLoading={doVoid.isPending}
            disabled={reason.trim().length < 5}
          >
            Void this run
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setVoiding(false)}>
            Keep it
          </Button>
        </div>
      ) : null}
      <details className="text-xs text-content-muted">
        <summary className="cursor-pointer">Employees</summary>
        <table className="mt-1 w-full tabular">
          <thead>
            <tr className="text-left">
              <th className="py-0.5 font-medium">Employee</th>
              <th className="py-0.5 text-right font-medium">Gross</th>
              <th className="py-0.5 text-right font-medium">CPP</th>
              <th className="py-0.5 text-right font-medium">EI</th>
              <th className="py-0.5 text-right font-medium">Tax</th>
              <th className="py-0.5 text-right font-medium">Other</th>
              <th className="py-0.5 text-right font-medium">Net</th>
            </tr>
          </thead>
          <tbody>
            {run.lines.map((line) => (
              <tr key={line.id}>
                <td className="py-0.5">{line.employeeName}</td>
                <td className="py-0.5 text-right">{formatMinor(line.grossMinor)}</td>
                <td className="py-0.5 text-right">{formatMinor(line.cppMinor)}</td>
                <td className="py-0.5 text-right">{formatMinor(line.eiMinor)}</td>
                <td className="py-0.5 text-right">{formatMinor(line.taxMinor)}</td>
                <td className="py-0.5 text-right">{formatMinor(line.otherMinor)}</td>
                <td className="py-0.5 text-right">{formatMinor(line.netMinor)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
}

function RunEditor({
  organizationId,
  initial,
  onDone,
  onCancel,
}: {
  organizationId: string;
  initial: PayrollRun | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const reference = useQuery(bookkeepingReferenceQuery(organizationId));
  const [payDate, setPayDate] = useState(initial?.payDate ?? today());
  const [periodStart, setPeriodStart] = useState(initial?.periodStart ?? '');
  const [periodEnd, setPeriodEnd] = useState(initial?.periodEnd ?? '');
  const [memo, setMemo] = useState(initial?.memo ?? '');
  const [lines, setLines] = useState<LineDraft[]>(
    initial === null || initial.lines.length === 0
      ? [blankLine(1)]
      : initial.lines.map((line, index) => ({
          key: index + 1,
          employeeName: line.employeeName,
          technicianId: line.technicianId ?? '',
          gross: minorToDollars(line.grossMinor),
          cpp: minorToDollars(line.cppMinor),
          ei: minorToDollars(line.eiMinor),
          tax: minorToDollars(line.taxMinor),
          other: line.otherMinor === '0' ? '' : minorToDollars(line.otherMinor),
          employerCpp: minorToDollars(line.employerCppMinor),
          employerEi: minorToDollars(line.employerEiMinor),
          employerOther:
            line.employerOtherMinor === '0' ? '' : minorToDollars(line.employerOtherMinor),
        })),
  );
  const [nextKey, setNextKey] = useState(lines.length + 1);
  const [error, setError] = useState<string | null>(null);

  const employees = reference.data?.employees ?? [];
  const update = (key: number, change: Partial<LineDraft>) =>
    setLines((before) => before.map((line) => (line.key === key ? { ...line, ...change } : line)));

  const net = (line: LineDraft): bigint =>
    cents(line.gross) - cents(line.cpp) - cents(line.ei) - cents(line.tax) - cents(line.other);
  const valid = (line: LineDraft) =>
    line.employeeName.trim() !== '' &&
    [
      line.gross,
      line.cpp,
      line.ei,
      line.tax,
      line.other,
      line.employerCpp,
      line.employerEi,
      line.employerOther,
    ].every((value) => value === '' || dollarsToMinor(value) !== null) &&
    cents(line.gross) > 0n &&
    net(line) >= 0n;
  const ready = payDate !== '' && lines.length > 0 && lines.every(valid);
  const totals = lines.reduce(
    (sum, line) => ({
      gross: sum.gross + cents(line.gross),
      net: sum.net + (valid(line) ? net(line) : 0n),
      employer:
        sum.employer +
        (line.employerCpp === '' ? cents(line.cpp) : cents(line.employerCpp)) +
        (line.employerEi === ''
          ? BigInt(Math.round(Number(cents(line.ei)) * 1.4))
          : cents(line.employerEi)) +
        cents(line.employerOther),
    }),
    { gross: 0n, net: 0n, employer: 0n },
  );

  const body = (): SavePayrollRunRequest => ({
    payDate,
    periodStart: periodStart === '' ? null : periodStart,
    periodEnd: periodEnd === '' ? null : periodEnd,
    memo: memo.trim() === '' ? null : memo.trim(),
    lines: lines.map((line) => ({
      employeeName: line.employeeName.trim(),
      technicianId: line.technicianId === '' ? null : line.technicianId,
      grossMinor: dollarsToMinor(line.gross) ?? '0',
      cppMinor: dollarsToMinor(line.cpp) ?? '0',
      eiMinor: dollarsToMinor(line.ei) ?? '0',
      taxMinor: dollarsToMinor(line.tax) ?? '0',
      otherMinor: dollarsToMinor(line.other) ?? '0',
      employerCppMinor: line.employerCpp === '' ? null : (dollarsToMinor(line.employerCpp) ?? '0'),
      employerEiMinor: line.employerEi === '' ? null : (dollarsToMinor(line.employerEi) ?? '0'),
      employerOtherMinor: dollarsToMinor(line.employerOther) ?? '0',
    })),
  });

  const save = useMutation({
    mutationFn: (andPost: boolean) =>
      savePayrollRun(organizationId, initial?.id ?? null, body()).then(async (saved) => {
        if (andPost) await postPayrollRun(organizationId, saved.runId);
        return saved;
      }),
    onSuccess: onDone,
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'The run was not saved.'),
  });

  const money = (
    label: string,
    value: string,
    onChange: (next: string) => void,
    placeholder?: string,
  ) => (
    <Input
      aria-label={label}
      inputMode="decimal"
      className="text-right"
      value={value}
      placeholder={placeholder}
      hasError={value !== '' && dollarsToMinor(value) === null}
      onChange={(event) => onChange(event.target.value)}
    />
  );

  return (
    <Card>
      <CardHeader
        title={initial === null ? 'Enter a pay run' : 'Edit the draft pay run'}
        description="Per employee, as the payroll system shows it: gross, then what was taken off. Net is worked out. The employer's CPP matches the employee's and EI is 1.4 times, unless you type otherwise."
      />
      <CardBody className="flex flex-col gap-4">
        {error === null ? null : <Alert tone="danger">{error}</Alert>}
        <div className="grid gap-4 sm:grid-cols-4">
          <Field label="Pay date" required>
            {({ inputProps }) => (
              <Input
                {...inputProps}
                type="date"
                value={payDate}
                onChange={(event) => setPayDate(event.target.value)}
              />
            )}
          </Field>
          <Field label="Period from">
            {({ inputProps }) => (
              <Input
                {...inputProps}
                type="date"
                value={periodStart}
                onChange={(event) => setPeriodStart(event.target.value)}
              />
            )}
          </Field>
          <Field label="Period to">
            {({ inputProps }) => (
              <Input
                {...inputProps}
                type="date"
                value={periodEnd}
                onChange={(event) => setPeriodEnd(event.target.value)}
              />
            )}
          </Field>
          <Field label="Memo">
            {({ inputProps }) => (
              <Input
                {...inputProps}
                value={memo}
                onChange={(event) => setMemo(event.target.value)}
                placeholder="Bi-weekly"
              />
            )}
          </Field>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[56rem] text-sm tabular">
            <thead>
              <tr className="text-left text-xs text-content-muted">
                <th className="py-1 font-medium">Employee</th>
                <th className="w-24 py-1 text-right font-medium">Gross</th>
                <th className="w-20 py-1 text-right font-medium">CPP</th>
                <th className="w-20 py-1 text-right font-medium">EI</th>
                <th className="w-24 py-1 text-right font-medium">Tax</th>
                <th className="w-20 py-1 text-right font-medium">Other</th>
                <th className="w-24 py-1 text-right font-medium">Net</th>
                <th className="w-20 py-1 text-right font-medium">Er. CPP</th>
                <th className="w-20 py-1 text-right font-medium">Er. EI</th>
                <th className="w-20 py-1 text-right font-medium">Er. other</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => (
                <tr key={line.key} className="align-top">
                  <td className="py-1 pr-2">
                    <Input
                      aria-label="Employee"
                      list={`employees-${String(line.key)}`}
                      value={line.employeeName}
                      onChange={(event) => {
                        const match = employees.find(
                          (employee) => employee.fullName === event.target.value,
                        );
                        update(line.key, {
                          employeeName: event.target.value,
                          technicianId: match?.id ?? '',
                        });
                      }}
                      placeholder="Name"
                    />
                    <datalist id={`employees-${String(line.key)}`}>
                      {employees.map((employee) => (
                        <option key={employee.id} value={employee.fullName} />
                      ))}
                    </datalist>
                  </td>
                  <td className="py-1 pl-1">
                    {money('Gross', line.gross, (next) => update(line.key, { gross: next }))}
                  </td>
                  <td className="py-1 pl-1">
                    {money('CPP', line.cpp, (next) => update(line.key, { cpp: next }))}
                  </td>
                  <td className="py-1 pl-1">
                    {money('EI', line.ei, (next) => update(line.key, { ei: next }))}
                  </td>
                  <td className="py-1 pl-1">
                    {money('Tax', line.tax, (next) => update(line.key, { tax: next }))}
                  </td>
                  <td className="py-1 pl-1">
                    {money('Other', line.other, (next) => update(line.key, { other: next }))}
                  </td>
                  <td
                    className={[
                      'py-3 pl-1 text-right font-medium',
                      net(line) < 0n ? 'text-danger' : '',
                    ].join(' ')}
                  >
                    {valid(line) || line.gross !== '' ? formatMinor(net(line).toString()) : '—'}
                  </td>
                  <td className="py-1 pl-1">
                    {money(
                      'Employer CPP',
                      line.employerCpp,
                      (next) => update(line.key, { employerCpp: next }),
                      line.cpp === '' ? '' : minorToDollars(cents(line.cpp).toString()),
                    )}
                  </td>
                  <td className="py-1 pl-1">
                    {money(
                      'Employer EI',
                      line.employerEi,
                      (next) => update(line.key, { employerEi: next }),
                      line.ei === ''
                        ? ''
                        : minorToDollars(String(Math.round(Number(cents(line.ei)) * 1.4))),
                    )}
                  </td>
                  <td className="py-1 pl-1">
                    {money('Employer other', line.employerOther, (next) =>
                      update(line.key, { employerOther: next }),
                    )}
                  </td>
                  <td className="py-3 text-right">
                    <button
                      type="button"
                      aria-label="Remove line"
                      className="text-content-muted hover:text-danger"
                      onClick={() =>
                        setLines((before) =>
                          before.length === 1
                            ? before
                            : before.filter((other) => other.key !== line.key),
                        )
                      }
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            className="text-sm text-brand-700 hover:underline"
            onClick={() => {
              setLines((before) => [...before, blankLine(nextKey)]);
              setNextKey(nextKey + 1);
            }}
          >
            Add an employee
          </button>
          <p className="text-sm text-content-muted">
            Gross {formatMinor(totals.gross.toString())} · net {formatMinor(totals.net.toString())}{' '}
            · employer {formatMinor(totals.employer.toString())} · total cost{' '}
            {formatMinor((totals.gross + totals.employer).toString())}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => save.mutate(true)}
            isLoading={save.isPending && save.variables}
            disabled={!ready}
          >
            Save and post
          </Button>
          <Button
            variant="secondary"
            onClick={() => save.mutate(false)}
            isLoading={save.isPending && !save.variables}
            disabled={!ready}
          >
            Save as draft
          </Button>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
